import { Fragment, useEffect } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import LogoutButton from "../buttons/Logout";
import { classNames } from "../utils/functions";
import { useTheme } from "../../context/ThemeContext";
import Avatar from "../common/Avatar";

const userNavigation = [
  { name: "Profile", href: "/profile" },
  { name: "Settings", href: "/settings" },
];

export default function ProfileMenu() {
  const user = useSelector((state: RootState) => state.user);
  const { theme, toggleTheme, setTheme } = useTheme();

  return (
    <Menu as="div" className="relative ml-3">
      <div>
        <Menu.Button className="group relative flex max-w-xs items-center rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500 p-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 hover:scale-110 transition-all duration-300">
          <span className="absolute -inset-1.5" />
          <span className="sr-only">Open user menu</span>
          <div className="rounded-full bg-white dark:bg-gray-900 p-0.5">
            <Avatar avatarUrl={user.avatar} name={user.name} size={8} />
          </div>

          {/* Subtle glow effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-indigo-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-md" />
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-300"
        enterFrom="transform opacity-0 scale-95 translate-y-1"
        enterTo="transform opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-200"
        leaveFrom="transform opacity-100 scale-100 translate-y-0"
        leaveTo="transform opacity-0 scale-95 translate-y-1"
      >
        <Menu.Items className="absolute right-0 z-50 mt-3 w-64 origin-top-right rounded-xl bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-2xl ring-1 ring-gray-200/50 dark:ring-gray-700/50 focus:outline-none overflow-hidden">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/3 via-purple-500/3 to-indigo-500/3 pointer-events-none" />

          {/* User info section */}
          <div className="relative px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50">
            {/* Decorative top gradient line */}
            <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-pink-500/30 to-transparent" />

            <p className="text-sm font-light tracking-wider uppercase text-gray-900 dark:text-gray-100 mb-1">
              {user?.name}
            </p>
            <p className="truncate text-xs font-light tracking-wide text-gray-500 dark:text-gray-400">
              {user.email}
            </p>
          </div>

          {/* Navigation items */}
          <div className="py-2">
            {userNavigation.map((item) => (
              <Menu.Item key={item.name}>
                {({ active }) => (
                  <a
                    href={item.href}
                    className={classNames(
                      active
                        ? "bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 text-gray-900 dark:text-white"
                        : "text-gray-700 dark:text-gray-300",
                      "group relative flex items-center px-6 py-3 text-sm font-light tracking-wider uppercase transition-all duration-300 hover:bg-gradient-to-r hover:from-pink-500/5 hover:via-purple-500/5 hover:to-indigo-500/5"
                    )}
                  >
                    {/* Decorative gradient line */}
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 group-hover:h-6 bg-gradient-to-b from-pink-500 via-purple-500 to-indigo-500 rounded-full transition-all duration-300" />

                    <span className="relative z-10 ml-2">{item.name}</span>

                    {/* Arrow indicator */}
                    <div className="ml-auto w-3 h-3 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </a>
                )}
              </Menu.Item>
            ))}
          </div>

          {/* Theme toggle */}
          <div className="border-t border-gray-200/50 dark:border-gray-700/50">
            <Menu.Item as="div" static>
              <button
                onClick={toggleTheme}
                className="group relative flex items-center w-full px-6 py-3 text-sm font-light tracking-wider uppercase transition-all duration-300 hover:bg-gradient-to-r hover:from-amber-500/5 hover:via-yellow-500/5 hover:to-amber-500/5 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                {/* Decorative gradient line */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 group-hover:h-6 bg-gradient-to-b from-amber-500 via-yellow-500 to-amber-600 rounded-full transition-all duration-300" />
                
                {/* Theme icon */}
                <div className="relative z-10 ml-2 mr-3">
                  {theme === 'light' ? (
                    // Moon icon for dark mode toggle
                    <svg className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  ) : (
                    // Sun icon for light mode toggle
                    <svg className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  )}
                </div>
                
                <span className="relative z-10">
                  {theme === 'light' ? 'Switch to Dark' : 'Switch to Light'}
                </span>
                
                {/* Arrow indicator */}
                <div className="ml-auto w-3 h-3 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </Menu.Item>
          </div>

          {/* Logout section */}
          <div className="border-t border-gray-200/50 dark:border-gray-700/50">
            <LogoutButton />
          </div>

          {/* Decorative bottom gradient line */}
          <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
